import React, { useEffect, useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { Upload, Save, ArrowLeft, X, AlertCircle, CheckCircle } from 'lucide-react';
import PageHeader from '../components/PageHeader';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { productApi, api } from '../../services/api';
import { useToast } from '../hooks/useToast';
import { compressImageFile, fileToDataUrl } from '../../utils/imageCompressor';

const AddProduct = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id: productId } = useParams();
  const isEdit = Boolean(productId);
  const { success, error: showError, warning } = useToast();
  const fileInputRef = useRef(null);
  const videoInputRef = useRef(null);
  const colorImageInputRef = useRef(null);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: '',
    price: '',
    marketPrice: '',
    stock: '',
    sku: '',
    material: '',
    dimensions: '',
    color: '',
    colorName: '',
    colorImageText: '',
    seoTitle: '',
    seoDescription: '',
    images: [],
    videos: [],
  });

  const [imagePreviews, setImagePreviews] = useState([]);
  const [videoPreviews, setVideoPreviews] = useState([]);
  const [colorImageFile, setColorImageFile] = useState(null);
  const [imageUploadError, setImageUploadError] = useState('');
  const [videoUploadError, setVideoUploadError] = useState('');
  const [variants, setVariants] = useState([]);
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    if (!isEdit) return;
    const loadProduct = async () => {
      try {
        const product = location.state?.product || (await productApi.get(productId)).data?.data;
        if (!product) return;
        setFormData({
          name: product.name || '',
          description: product.description || '',
          category: product.category || '',
          price: product.price || '',
          marketPrice: product.marketPrice || '',
          stock: product.stock || '',
          sku: product.sku || '',
          material: product.material || '',
          dimensions: product.dimensions || '',
          color: product.color || '',
          colorName: product.colorName || product.color || '',
          colorImageText: product.colorImage?.url || product.colorImage || '',
          seoTitle: product.seoTitle || '',
          seoDescription: product.seoDescription || '',
          images: [],
          videos: [],
        });
        setImagePreviews((product.images || []).slice(0, 8).map((image, index) => ({
          id: image.publicId || image.url || `${product._id}-${index}`,
          name: image.alt || `Image ${index + 1}`,
          preview: image.url || image,
          existingUrl: image.url || image,
          publicId: image.publicId
        })));
        setVideoPreviews((product.videos || []).slice(0, 3).map((video, index) => ({
          id: video.publicId || video.url || `${product._id}-video-${index}`,
          name: video.title || `Video ${index + 1}`,
          preview: video.url || video,
          existingUrl: video.url || video,
          publicId: video.publicId
        })));
        setVariants((product.variants || []).map((variant, index) => ({
          id: variant._id || variant.id || `${product._id}-variant-${index}`,
          _id: variant._id,
          color: variant.color || '',
          colorName: variant.colorName || variant.color || '',
          colorImageText: variant.colorImage?.url || variant.colorImage || '',
          colorImageFile: null,
          size: variant.size || '',
          sku: variant.sku || '',
          price: variant.price ?? '',
          marketPrice: variant.marketPrice ?? '',
          stock: variant.stock ?? '',
          material: variant.material || '',
          dimensions: variant.dimensions || '',
          specification: variant.specification || '',
          imagesText: (variant.images || []).map((image) => image.url || image).filter(Boolean).join('\n'),
          videosText: (variant.videos || []).map((video) => video.url || video).filter(Boolean).join('\n'),
          imageFiles: [],
          videoFiles: []
        })));
      } catch (err) {
        showError(err.response?.data?.message || 'Unable to load product for editing');
      }
    };
    loadProduct();
  }, [isEdit, location.state, productId, showError]);

  const validateImages = (files) => {
    const MAX_FILE_SIZE = 15 * 1024 * 1024;
    const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

    const errors = [];
    files.forEach(file => {
      if (!ALLOWED_TYPES.includes(file.type)) {
        errors.push(`${file.name} - Invalid format (JPG, PNG, WEBP only)`);
      }
      if (file.size > MAX_FILE_SIZE) {
        errors.push(`${file.name} - File too large (Max 15MB)`);
      }
    });

    return errors;
  };

  const validateVideos = (files) => {
    const MAX_FILE_SIZE = 100 * 1024 * 1024;
    const ALLOWED_TYPES = ['video/mp4', 'video/webm', 'video/quicktime'];

    const errors = [];
    files.forEach(file => {
      if (!ALLOWED_TYPES.includes(file.type)) {
        errors.push(`${file.name} - Invalid format (MP4, WEBM, MOV only)`);
      }
      if (file.size > MAX_FILE_SIZE) {
        errors.push(`${file.name} - File too large (Max 100MB)`);
      }
    });

    return errors;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    if (imagePreviews.length + files.length > 8) {
      setImageUploadError('You can upload a maximum of 8 product images.');
      return;
    }

    const errors = validateImages(files);
    if (errors.length > 0) {
      setImageUploadError(errors.join(', '));
      return;
    }

    setImageUploadError('');
    const newPreviews = files.map(file => ({
      id: Date.now() + Math.random(),
      name: file.name,
      file: file,
      preview: URL.createObjectURL(file)
    }));

    setFormData(prev => ({
      ...prev,
      images: [...prev.images, ...files].slice(0, 8)
    }));
    setImagePreviews(prev => [...prev, ...newPreviews].slice(0, 8));
  };

  const removeImage = (id) => {
    setImagePreviews(prev => {
      const index = prev.findIndex(img => img.id === id);
      if (index >= 0) {
        if (!prev[index].existingUrl) URL.revokeObjectURL(prev[index].preview);
      }
      return prev.filter(img => img.id !== id);
    });
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== imagePreviews.findIndex(img => img.id === id))
    }));
  };

  const handleMainColorImage = (files) => {
    const selectedFile = Array.from(files || [])[0];
    if (!selectedFile) return;
    const errors = validateImages([selectedFile]);
    if (errors.length > 0) {
      showError(errors.join(', '));
      return;
    }
    if (colorImageFile?.preview) URL.revokeObjectURL(colorImageFile.preview);
    setColorImageFile({
      file: selectedFile,
      name: selectedFile.name,
      preview: URL.createObjectURL(selectedFile)
    });
  };

  const removeMainColorImage = () => {
    if (colorImageFile?.preview) URL.revokeObjectURL(colorImageFile.preview);
    setColorImageFile(null);
    setFormData(prev => ({ ...prev, colorImageText: '' }));
    if (colorImageInputRef.current) colorImageInputRef.current.value = '';
  };

  const handleVideoUpload = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    if (videoPreviews.length + files.length > 3) {
      setVideoUploadError('You can upload a maximum of 3 product videos.');
      return;
    }

    const errors = validateVideos(files);
    if (errors.length > 0) {
      setVideoUploadError(errors.join(', '));
      return;
    }

    setVideoUploadError('');
    const newPreviews = files.map(file => ({
      id: Date.now() + Math.random(),
      name: file.name,
      file,
      preview: URL.createObjectURL(file)
    }));

    setFormData(prev => ({
      ...prev,
      videos: [...prev.videos, ...files].slice(0, 3)
    }));
    setVideoPreviews(prev => [...prev, ...newPreviews].slice(0, 3));
  };

  const removeVideo = (id) => {
    setVideoPreviews(prev => {
      const index = prev.findIndex(video => video.id === id);
      if (index >= 0 && !prev[index].existingUrl) URL.revokeObjectURL(prev[index].preview);
      return prev.filter(video => video.id !== id);
    });
    setFormData(prev => ({
      ...prev,
      videos: prev.videos.filter((_, i) => i !== videoPreviews.findIndex(video => video.id === id))
    }));
  };

  const addVariant = () => {
    setVariants(prev => ([
      ...prev,
      {
        id: Date.now() + Math.random(),
        color: '',
        colorName: '',
        colorImageText: '',
        colorImageFile: null,
        size: '',
        sku: '',
        price: '',
        marketPrice: '',
        stock: '',
        material: '',
        dimensions: '',
        specification: '',
        imagesText: '',
        videosText: '',
        imageFiles: [],
        videoFiles: []
      }
    ]));
  };

  const updateVariant = (id, field, value) => {
    setVariants(prev => prev.map(variant => (variant.id === id ? { ...variant, [field]: value } : variant)));
  };

  const removeVariant = (id) => {
    setVariants(prev => prev.filter(variant => variant.id !== id));
  };

  const handleVariantFiles = (id, field, files) => {
    const selectedFiles = Array.from(files || []);
    if (selectedFiles.length === 0) return;
    const errors = field === 'imageFiles' ? validateImages(selectedFiles) : validateVideos(selectedFiles);
    if (errors.length > 0) {
      showError(errors.join(', '));
      return;
    }
    setVariants(prev => prev.map(variant => {
      if (variant.id !== id) return variant;
      const nextFiles = [...(variant[field] || []), ...selectedFiles].slice(0, field === 'imageFiles' ? 8 : 3);
      return { ...variant, [field]: nextFiles };
    }));
  };

  const handleVariantColorImage = (id, files) => {
    const selectedFile = Array.from(files || [])[0];
    if (!selectedFile) return;
    const errors = validateImages([selectedFile]);
    if (errors.length > 0) {
      showError(errors.join(', '));
      return;
    }
    setVariants(prev => prev.map(variant => (variant.id === id ? { ...variant, colorImageFile: selectedFile } : variant)));
  };

  const toMediaList = (value) =>
    String(value || '')
      .split('\n')
      .map(url => url.trim())
      .filter(Boolean)
      .map(url => ({ url }));

  const validateForm = () => {
    const errors = {};

    if (!formData.name.trim()) errors.name = 'Product name is required';
    if (!formData.category) errors.category = 'Category is required';
    if (!formData.description.trim()) errors.description = 'Description is required';
    if (!formData.price || Number(formData.price) <= 0) errors.price = 'Valid price is required';
    if (formData.stock === '' || Number(formData.stock) < 0) errors.stock = 'Valid stock is required';
    if (!formData.sku.trim()) errors.sku = 'SKU is required';
    if (imagePreviews.length === 0) errors.images = 'At least one image is required';

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      showError('Please fix all errors before saving');
      return;
    }

    setSaving(true);

    try {
      const imageUrls = [];
      const videoUrls = [];

      for (const imagePreview of imagePreviews) {
        if (imagePreview.existingUrl) {
          imageUrls.push({ url: imagePreview.existingUrl, publicId: imagePreview.publicId, alt: imagePreview.name });
          continue;
        }
        
        let fileToUpload = imagePreview.file;
        if (fileToUpload) {
          fileToUpload = await compressImageFile(fileToUpload, 1200, 1200, 0.8);
        }

        const formDataImg = new FormData();
        formDataImg.append('image', fileToUpload);
        formDataImg.append('file', fileToUpload);

        try {
          const uploadResponse = await api.post('/upload/image', formDataImg, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          const uploadedUrl = uploadResponse.data?.data?.url || uploadResponse.data?.url;
          if (uploadedUrl) {
            imageUrls.push({ url: uploadedUrl, publicId: uploadResponse.data?.data?.publicId, alt: formData.name.trim() });
          } else {
            const dataUrl = await fileToDataUrl(fileToUpload);
            imageUrls.push({ url: dataUrl, alt: formData.name.trim() });
          }
        } catch (uploadErr) {
          console.warn('Network or endpoint upload issue, using base64 data URL fallback:', uploadErr);
          try {
            const dataUrl = await fileToDataUrl(fileToUpload);
            imageUrls.push({ url: dataUrl, alt: formData.name.trim() });
          } catch (fallbackErr) {
            warning(`Failed to attach ${imagePreview.name}`);
          }
        }
      }

      for (const videoPreview of videoPreviews) {
        if (videoPreview.existingUrl) {
          videoUrls.push({ url: videoPreview.existingUrl, publicId: videoPreview.publicId, title: videoPreview.name });
          continue;
        }
        const formDataVideo = new FormData();
        formDataVideo.append('video', videoPreview.file);

        try {
          const uploadResponse = await api.post('/upload/video', formDataVideo, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          const uploadedUrl = uploadResponse.data?.data?.url || uploadResponse.data?.url;
          if (uploadedUrl) {
            videoUrls.push({ url: uploadedUrl, publicId: uploadResponse.data?.data?.publicId, title: videoPreview.name });
          }
        } catch (uploadErr) {
          console.error('Video upload failed:', uploadErr);
          warning(`Failed to upload ${videoPreview.name}`);
        }
      }

      let mainColorImage = formData.colorImageText
        ? { url: formData.colorImageText.trim(), alt: formData.colorName || formData.color || formData.name.trim() }
        : undefined;

      if (colorImageFile?.file) {
        const formDataImg = new FormData();
        formDataImg.append('image', colorImageFile.file);
        try {
          const uploadResponse = await api.post('/upload/image', formDataImg, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          const uploadedUrl = uploadResponse.data?.data?.url || uploadResponse.data?.url;
          if (uploadedUrl) {
            mainColorImage = {
              url: uploadedUrl,
              publicId: uploadResponse.data?.data?.publicId,
              alt: formData.colorName || formData.color || colorImageFile.name
            };
          }
        } catch (uploadErr) {
          console.error('Main color image upload failed:', uploadErr);
          warning(`Failed to upload color image ${colorImageFile.name}`);
        }
      }

      const productVariants = [];
      for (const variant of variants.filter(variant => variant.color || variant.colorName || variant.colorImageText || variant.colorImageFile || variant.size || variant.sku || variant.price || variant.stock)) {
        const variantImages = toMediaList(variant.imagesText);
        const variantVideos = toMediaList(variant.videosText);
        let colorImage = variant.colorImageText ? { url: variant.colorImageText.trim(), alt: variant.colorName || variant.color } : undefined;

        if (variant.colorImageFile) {
          const formDataImg = new FormData();
          formDataImg.append('image', variant.colorImageFile);
          try {
            const uploadResponse = await api.post('/upload/image', formDataImg, {
              headers: { 'Content-Type': 'multipart/form-data' }
            });
            const uploadedUrl = uploadResponse.data?.data?.url || uploadResponse.data?.url;
            if (uploadedUrl) {
              colorImage = {
                url: uploadedUrl,
                publicId: uploadResponse.data?.data?.publicId,
                alt: variant.colorName || variant.color || variant.colorImageFile.name
              };
            }
          } catch (uploadErr) {
            console.error('Variant color image upload failed:', uploadErr);
            warning(`Failed to upload color image ${variant.colorImageFile.name}`);
          }
        }

        for (const file of variant.imageFiles || []) {
          const formDataImg = new FormData();
          formDataImg.append('image', file);
          try {
            const uploadResponse = await api.post('/upload/image', formDataImg, {
              headers: { 'Content-Type': 'multipart/form-data' }
            });
            const uploadedUrl = uploadResponse.data?.data?.url || uploadResponse.data?.url;
            if (uploadedUrl) {
              variantImages.push({ url: uploadedUrl, publicId: uploadResponse.data?.data?.publicId, alt: `${variant.color} ${variant.size}`.trim() });
            }
          } catch (uploadErr) {
            console.error('Variant image upload failed:', uploadErr);
            warning(`Failed to upload variant image ${file.name}`);
          }
        }

        for (const file of variant.videoFiles || []) {
          const formDataVideo = new FormData();
          formDataVideo.append('video', file);
          try {
            const uploadResponse = await api.post('/upload/video', formDataVideo, {
              headers: { 'Content-Type': 'multipart/form-data' }
            });
            const uploadedUrl = uploadResponse.data?.data?.url || uploadResponse.data?.url;
            if (uploadedUrl) {
              variantVideos.push({ url: uploadedUrl, publicId: uploadResponse.data?.data?.publicId, title: file.name });
            }
          } catch (uploadErr) {
            console.error('Variant video upload failed:', uploadErr);
            warning(`Failed to upload variant video ${file.name}`);
          }
        }

        productVariants.push({
          ...(variant._id && { _id: variant._id }),
          color: variant.color.trim(),
          colorName: (variant.colorName || variant.color).trim(),
          ...(colorImage && { colorImage }),
          size: variant.size.trim(),
          name: [variant.colorName || variant.color, variant.size].filter(Boolean).join(' / '),
          sku: variant.sku.trim(),
          price: Number(variant.price || formData.price),
          ...(variant.marketPrice && Number(variant.marketPrice) > 0 && { marketPrice: Number(variant.marketPrice) }),
          stock: Number(variant.stock || 0),
          ...(variant.material && { material: variant.material.trim() }),
          ...(variant.dimensions && { dimensions: variant.dimensions.trim() }),
          ...(variant.specification && { specification: variant.specification.trim() }),
          images: variantImages,
          videos: variantVideos
        });
      }

      const productData = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        category: formData.category,
        price: Number(formData.price),
        ...(formData.marketPrice && Number(formData.marketPrice) > 0 && { marketPrice: Number(formData.marketPrice) }),
        stock: Number(formData.stock || 0),
        sku: formData.sku.trim(),
        ...(formData.material && { material: formData.material.trim() }),
        ...(formData.dimensions && { dimensions: formData.dimensions.trim() }),
        ...(formData.color && { color: formData.color.trim() }),
        ...(formData.colorName && { colorName: formData.colorName.trim() }),
        ...(mainColorImage ? { colorImage: mainColorImage } : isEdit ? { colorImage: null } : {}),
        images: imageUrls.map((image, index) => ({ ...image, isThumbnail: index === 0 })),
        videos: videoUrls,
        variants: productVariants,
        isActive: true
      };

      if (isEdit) {
        await productApi.update(productId, productData);
        success('Product updated successfully!');
      } else {
        await productApi.create(productData);
        success('Product created successfully!');
      }
      setTimeout(() => navigate('/admin/products', { replace: true }), 500);
    } catch (err) {
      const errorMsg = err.response?.data?.message || err.message || 'Unable to save product';
      showError(errorMsg);
      console.error('Save error:', err);
    } finally {
      setSaving(false);
    }
  };

  const steps = [
    { num: 1, title: 'Basic Info' },
    { num: 2, title: 'Details' },
    { num: 3, title: 'Variants' },
    { num: 4, title: 'Media & SEO' },
  ];

  const mainColorImagePreview = colorImageFile?.preview || formData.colorImageText;

  return (
    <div className="space-y-6">
      <PageHeader
        title={isEdit ? 'Edit Product' : 'Add New Product'}
        description={isEdit ? 'Update existing furniture product' : 'Create a new furniture product'}
        breadcrumbs={['Home', 'Products', isEdit ? 'Edit' : 'Add']}
        action={
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-50 transition text-gray-700"
          >
            <ArrowLeft size={18} />
            Back
          </button>
        }
      />

      {/* Steps */}
      <div className="flex items-center justify-center gap-2 md:gap-4 mb-8 flex-wrap">
        {steps.map((s, idx) => (
          <motion.div key={s.num} className="flex items-center">
            <motion.div
              whileHover={{ scale: 1.1 }}
              onClick={() => setStep(s.num)}
              className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold cursor-pointer transition ${
                step === s.num
                  ? 'bg-amber-600 text-white'
                  : step > s.num
                  ? 'bg-green-100 text-green-700'
                  : 'bg-gray-100 text-gray-600'
              }`}
            >
              {step > s.num ? '✓' : s.num}
            </motion.div>
            <p className="ml-2 text-sm font-medium text-gray-700 hidden sm:block">{s.title}</p>
            {idx < steps.length - 1 && (
              <div className="w-6 md:w-12 h-1 mx-2 md:mx-4 bg-gray-200 hidden sm:block" />
            )}
          </motion.div>
        ))}
      </div>

      {/* Form */}
      <motion.form
        onSubmit={handleSubmit}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-xl border border-gray-100 p-6"
      >
        {/* Step 1: Basic Info */}
        {step === 1 && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Product Name *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="e.g., Premium Leather Sofa"
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:border-transparent outline-none transition ${
                    formErrors.name ? 'border-red-500 focus:ring-red-500' : 'border-gray-200 focus:ring-amber-500'
                  }`}
                />
                {formErrors.name && <p className="text-sm text-red-600 mt-1">{formErrors.name}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Category *
                </label>
                <select
                  name="category"
                  value={formData.category}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:border-transparent outline-none transition ${
                    formErrors.category ? 'border-red-500 focus:ring-red-500' : 'border-gray-200 focus:ring-amber-500'
                  }`}
                >
                  <option value="">Select Category</option>
                  <option value="sofas">Sofas</option>
                  <option value="beds">Beds</option>
                  <option value="dining">Dining Tables</option>
                  <option value="chairs">Chairs</option>
                  <option value="storage">Wardrobes / Storage</option>
                  <option value="study">Office Furniture</option>
                </select>
                {formErrors.category && <p className="text-sm text-red-600 mt-1">{formErrors.category}</p>}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description *
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Describe your product in detail..."
                rows={4}
                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:border-transparent outline-none transition ${
                  formErrors.description ? 'border-red-500 focus:ring-red-500' : 'border-gray-200 focus:ring-amber-500'
                }`}
              />
              {formErrors.description && <p className="text-sm text-red-600 mt-1">{formErrors.description}</p>}
            </div>

            <div className="flex gap-4 justify-end">
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="px-6 py-2 border border-gray-200 rounded-lg hover:bg-gray-50 transition text-gray-700 font-medium"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => setStep(2)}
                className="px-6 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition font-medium"
              >
                Next
              </button>
            </div>
          </div>
        )}

        {/* Step 2: Details */}
        {step === 2 && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Price (₹) *
                </label>
                <input
                  type="number"
                  name="price"
                  value={formData.price}
                  onChange={handleInputChange}
                  placeholder="45000"
                  step="0.01"
                  min="0"
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:border-transparent outline-none transition ${
                    formErrors.price ? 'border-red-500 focus:ring-red-500' : 'border-gray-200 focus:ring-amber-500'
                  }`}
                />
                {formErrors.price && <p className="text-sm text-red-600 mt-1">{formErrors.price}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Market Price (₹)
                </label>
                <input
                  type="number"
                  name="marketPrice"
                  value={formData.marketPrice}
                  onChange={handleInputChange}
                  placeholder="50000"
                  step="0.01"
                  min="0"
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Stock Quantity *
                </label>
                <input
                  type="number"
                  name="stock"
                  value={formData.stock}
                  onChange={handleInputChange}
                  placeholder="25"
                  min="0"
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:border-transparent outline-none transition ${
                    formErrors.stock ? 'border-red-500 focus:ring-red-500' : 'border-gray-200 focus:ring-amber-500'
                  }`}
                />
                {formErrors.stock && <p className="text-sm text-red-600 mt-1">{formErrors.stock}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  SKU *
                </label>
                <input
                  type="text"
                  name="sku"
                  value={formData.sku}
                  onChange={handleInputChange}
                  placeholder="SOFA-001"
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:border-transparent outline-none transition ${
                    formErrors.sku ? 'border-red-500 focus:ring-red-500' : 'border-gray-200 focus:ring-amber-500'
                  }`}
                />
                {formErrors.sku && <p className="text-sm text-red-600 mt-1">{formErrors.sku}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Material
                </label>
                <input
                  type="text"
                  name="material"
                  value={formData.material}
                  onChange={handleInputChange}
                  placeholder="e.g., Premium Leather"
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Colour family / code
                </label>
                <input
                  type="text"
                  name="color"
                  value={formData.color}
                  onChange={handleInputChange}
                  placeholder="e.g., Black"
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Colour display name
                </label>
                <input
                  type="text"
                  name="colorName"
                  value={formData.colorName}
                  onChange={handleInputChange}
                  placeholder="e.g., Oak Black"
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent outline-none"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-[1fr_140px] gap-4 rounded-lg border border-gray-200 bg-gray-50/60 p-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Main colour image
                </label>
                <input
                  type="url"
                  name="colorImageText"
                  value={formData.colorImageText}
                  onChange={handleInputChange}
                  placeholder="Paste a colour swatch or finish image URL"
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent outline-none"
                />
                <input
                  ref={colorImageInputRef}
                  type="file"
                  accept=".jpg,.jpeg,.png,.webp"
                  onChange={(e) => handleMainColorImage(e.target.files)}
                  className="mt-3 block w-full text-sm text-gray-600 file:mr-4 file:rounded-lg file:border-0 file:bg-amber-100 file:px-4 file:py-2 file:text-amber-800 hover:file:bg-amber-200"
                />
                <p className="mt-2 text-xs text-gray-500">
                  Optional swatch/finish image. Supported: JPG, PNG, WEBP. Maximum image size: 15MB.
                </p>
                {colorImageFile && (
                  <p className="mt-2 text-xs text-gray-600">New colour image selected: {colorImageFile.name}</p>
                )}
              </div>
              <div className="flex flex-col items-start md:items-end gap-2">
                {mainColorImagePreview ? (
                  <>
                    <div className="h-28 w-28 overflow-hidden rounded-lg border border-gray-200 bg-white">
                      <img
                        src={mainColorImagePreview}
                        alt={formData.colorName || formData.color || 'Colour preview'}
                        className="h-full w-full object-cover"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={removeMainColorImage}
                      className="text-xs font-medium text-red-600 hover:text-red-700"
                    >
                      Remove colour image
                    </button>
                  </>
                ) : (
                  <div className="flex h-28 w-28 items-center justify-center rounded-lg border border-dashed border-gray-300 bg-white px-3 text-center text-xs text-gray-500">
                    Colour image preview
                  </div>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Dimensions
              </label>
              <input
                type="text"
                name="dimensions"
                value={formData.dimensions}
                onChange={handleInputChange}
                placeholder="e.g., 220cm x 90cm x 85cm"
                className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent outline-none"
              />
            </div>

            <div className="flex gap-4 justify-end">
              <button
                type="button"
                onClick={() => setStep(1)}
                className="px-6 py-2 border border-gray-200 rounded-lg hover:bg-gray-50 transition text-gray-700 font-medium"
              >
                Back
              </button>
              <button
                type="button"
                onClick={() => setStep(3)}
                className="px-6 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition font-medium"
              >
                Next
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Variants */}
        {step === 3 && (
          <div className="space-y-6">
            <div className="flex items-center justify-between gap-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Colour, Size & Price Variants</h3>
                <p className="text-sm text-gray-500 mt-1">Use variants when the same product has different colours, sizes, media, prices or stock.</p>
              </div>
              <button
                type="button"
                onClick={addVariant}
                className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition font-medium"
              >
                Add Variant
              </button>
            </div>

            {variants.length === 0 ? (
              <div className="border border-dashed border-gray-300 rounded-lg p-6 text-center text-gray-500">
                No variants added. The base product price, media and stock will be used.
              </div>
            ) : (
              <div className="space-y-4">
                {variants.map((variant, index) => (
                  <div key={variant.id} className="border border-gray-200 rounded-lg p-4 space-y-4">
                    <div className="flex items-center justify-between gap-3">
                      <h4 className="font-semibold text-gray-900">Variant {index + 1}</h4>
                      <button
                        type="button"
                        onClick={() => removeVariant(variant.id)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition"
                        aria-label="Remove variant"
                      >
                        <X size={18} />
                      </button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Colour code / family</label>
                        <input
                          type="text"
                          value={variant.color}
                          onChange={(e) => updateVariant(variant.id, 'color', e.target.value)}
                          placeholder="Grey"
                          className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Colour display name *</label>
                        <input
                          type="text"
                          value={variant.colorName}
                          onChange={(e) => updateVariant(variant.id, 'colorName', e.target.value)}
                          placeholder="Silver Grey"
                          className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Size *</label>
                        <input
                          type="text"
                          value={variant.size}
                          onChange={(e) => updateVariant(variant.id, 'size', e.target.value)}
                          placeholder="3 Seater"
                          className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent outline-none"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-2">Colour selection image URL</label>
                        <input
                          type="url"
                          value={variant.colorImageText}
                          onChange={(e) => updateVariant(variant.id, 'colorImageText', e.target.value)}
                          placeholder="Paste a close-up colour swatch or product colour image URL"
                          className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent outline-none"
                        />
                        <input
                          type="file"
                          accept=".jpg,.jpeg,.png,.webp"
                          onChange={(e) => handleVariantColorImage(variant.id, e.target.files)}
                          className="mt-3 block w-full text-sm text-gray-600 file:mr-4 file:rounded-lg file:border-0 file:bg-amber-100 file:px-4 file:py-2 file:text-amber-800 hover:file:bg-amber-200"
                        />
                        <p className="mt-2 text-xs text-gray-500">Supported: JPG, PNG, WEBP. Maximum image size: 15MB.</p>
                        {variant.colorImageFile && (
                          <p className="mt-2 text-xs text-gray-500">New colour image selected: {variant.colorImageFile.name}</p>
                        )}
                      </div>
                      <div className="flex items-end">
                        {(variant.colorImageFile || variant.colorImageText) ? (
                          <div className="h-24 w-24 overflow-hidden rounded-lg border border-gray-200 bg-gray-50">
                            <img
                              src={variant.colorImageFile ? URL.createObjectURL(variant.colorImageFile) : variant.colorImageText}
                              alt={variant.colorName || variant.color || 'Colour preview'}
                              className="h-full w-full object-cover"
                            />
                          </div>
                        ) : (
                          <div className="flex h-24 w-full items-center justify-center rounded-lg border border-dashed border-gray-300 text-xs text-gray-500">
                            Colour image preview
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Variant SKU</label>
                        <input
                          type="text"
                          value={variant.sku}
                          onChange={(e) => updateVariant(variant.id, 'sku', e.target.value)}
                          placeholder="SOFA-GREY-3S"
                          className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Price *</label>
                        <input
                          type="number"
                          value={variant.price}
                          onChange={(e) => updateVariant(variant.id, 'price', e.target.value)}
                          min="0"
                          step="0.01"
                          placeholder={formData.price || '54990'}
                          className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Market Price</label>
                        <input
                          type="number"
                          value={variant.marketPrice}
                          onChange={(e) => updateVariant(variant.id, 'marketPrice', e.target.value)}
                          min="0"
                          step="0.01"
                          placeholder={formData.marketPrice || '98990'}
                          className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Variant Stock *</label>
                        <input
                          type="number"
                          value={variant.stock}
                          onChange={(e) => updateVariant(variant.id, 'stock', e.target.value)}
                          min="0"
                          placeholder="12"
                          className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Material</label>
                        <input
                          type="text"
                          value={variant.material}
                          onChange={(e) => updateVariant(variant.id, 'material', e.target.value)}
                          placeholder="Top-grain leather"
                          className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Dimensions</label>
                        <input
                          type="text"
                          value={variant.dimensions}
                          onChange={(e) => updateVariant(variant.id, 'dimensions', e.target.value)}
                          placeholder="210 x 90 x 85 cm"
                          className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent outline-none"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Specification</label>
                      <textarea
                        value={variant.specification}
                        onChange={(e) => updateVariant(variant.id, 'specification', e.target.value)}
                        rows={2}
                        placeholder="Variant-specific details shown on the product page"
                        className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent outline-none"
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Variant Image URLs</label>
                        <textarea
                          value={variant.imagesText}
                          onChange={(e) => updateVariant(variant.id, 'imagesText', e.target.value)}
                          rows={3}
                          placeholder="Paste one uploaded image URL per line"
                          className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent outline-none"
                        />
                        <input
                          type="file"
                          multiple
                          accept=".jpg,.jpeg,.png,.webp"
                          onChange={(e) => handleVariantFiles(variant.id, 'imageFiles', e.target.files)}
                          className="mt-3 block w-full text-sm text-gray-600 file:mr-4 file:rounded-lg file:border-0 file:bg-amber-100 file:px-4 file:py-2 file:text-amber-800 hover:file:bg-amber-200"
                        />
                        <p className="mt-2 text-xs text-gray-500">Up to 5 images. Supported: JPG, PNG, WEBP. Maximum image size: 15MB each.</p>
                        {variant.imageFiles?.length > 0 && (
                          <p className="mt-2 text-xs text-gray-500">{variant.imageFiles.length} new image(s) selected</p>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Variant Video URLs</label>
                        <textarea
                          value={variant.videosText}
                          onChange={(e) => updateVariant(variant.id, 'videosText', e.target.value)}
                          rows={3}
                          placeholder="Paste one uploaded video URL per line"
                          className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent outline-none"
                        />
                        <input
                          type="file"
                          multiple
                          accept=".mp4,.webm,.mov"
                          onChange={(e) => handleVariantFiles(variant.id, 'videoFiles', e.target.files)}
                          className="mt-3 block w-full text-sm text-gray-600 file:mr-4 file:rounded-lg file:border-0 file:bg-amber-100 file:px-4 file:py-2 file:text-amber-800 hover:file:bg-amber-200"
                        />
                        <p className="mt-2 text-xs text-gray-500">Up to 3 videos. Supported: MP4, WEBM, MOV. Maximum video size: 100MB each.</p>
                        {variant.videoFiles?.length > 0 && (
                          <p className="mt-2 text-xs text-gray-500">{variant.videoFiles.length} new video(s) selected</p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="flex gap-4 justify-end">
              <button
                type="button"
                onClick={() => setStep(2)}
                className="px-6 py-2 border border-gray-200 rounded-lg hover:bg-gray-50 transition text-gray-700 font-medium"
              >
                Back
              </button>
              <button
                type="button"
                onClick={() => setStep(4)}
                className="px-6 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition font-medium"
              >
                Next
              </button>
            </div>
          </div>
        )}

        {/* Step 4: Media & SEO */}
        {step === 4 && (
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Product Images * (JPG, PNG, WEBP - Max 15MB each, up to 5)
              </label>

              {/* Image Upload Area */}
              <div
                onClick={() => fileInputRef.current?.click()}
                className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-amber-500 transition cursor-pointer hover:bg-amber-50"
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  onChange={handleImageUpload}
                  accept=".jpg,.jpeg,.png,.webp"
                  className="hidden"
                />
                <Upload className="mx-auto text-gray-400 mb-2" size={32} />
                <p className="text-gray-600 font-medium">Click to upload or drag images here</p>
                <p className="text-xs text-gray-500 mt-1">Supported: JPG, PNG, WEBP (Max 15MB each, up to 5)</p>
              </div>

              {imageUploadError && (
                <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg flex gap-2 text-sm text-red-700">
                  <AlertCircle size={18} className="flex-shrink-0 mt-0.5" />
                  <div>{imageUploadError}</div>
                </div>
              )}

              {formErrors.images && (
                <p className="text-sm text-red-600 mt-2">{formErrors.images}</p>
              )}

              {/* Image Previews */}
              {imagePreviews.length > 0 && (
                <div className="mt-4 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {imagePreviews.map((img, idx) => (
                    <motion.div
                      key={img.id}
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className="relative group"
                    >
                      <img
                        src={img.preview}
                        alt={`Preview ${idx}`}
                        className="w-full h-24 object-cover rounded-lg border border-gray-200"
                      />
                      <div className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition">
                        <button
                          type="button"
                          onClick={() => removeImage(img.id)}
                          className="p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition"
                        >
                          <X size={16} />
                        </button>
                      </div>
                      {idx === 0 && (
                        <div className="absolute bottom-1 left-1 bg-amber-500 text-white text-xs px-2 py-1 rounded">
                          Thumbnail
                        </div>
                      )}
                    </motion.div>
                  ))}
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Product Videos (MP4, WEBM, MOV - Max 100MB each, up to 3)
              </label>

              <div
                onClick={() => videoInputRef.current?.click()}
                className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-amber-500 transition cursor-pointer hover:bg-amber-50"
              >
                <input
                  ref={videoInputRef}
                  type="file"
                  multiple
                  onChange={handleVideoUpload}
                  accept=".mp4,.webm,.mov"
                  className="hidden"
                />
                <Upload className="mx-auto text-gray-400 mb-2" size={32} />
                <p className="text-gray-600 font-medium">Click to upload product videos</p>
                <p className="text-xs text-gray-500 mt-1">Supported: MP4, WEBM, MOV (Max 100MB each, up to 3)</p>
              </div>

              {videoUploadError && (
                <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg flex gap-2 text-sm text-red-700">
                  <AlertCircle size={18} className="flex-shrink-0 mt-0.5" />
                  <div>{videoUploadError}</div>
                </div>
              )}

              {videoPreviews.length > 0 && (
                <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
                  {videoPreviews.map((video, idx) => (
                    <motion.div
                      key={video.id}
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className="relative group border border-gray-200 rounded-lg overflow-hidden bg-gray-50"
                    >
                      <video
                        src={video.preview}
                        className="w-full h-32 object-cover"
                        controls
                      />
                      <div className="p-2 text-xs text-gray-600 truncate">{video.name || `Video ${idx + 1}`}</div>
                      <div className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition">
                        <button
                          type="button"
                          onClick={() => removeVideo(video.id)}
                          className="p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition"
                        >
                          <X size={16} />
                        </button>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  SEO Title
                </label>
                <input
                  type="text"
                  name="seoTitle"
                  value={formData.seoTitle}
                  onChange={handleInputChange}
                  placeholder="SEO Title for search engines"
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  SEO Description
                </label>
                <input
                  type="text"
                  name="seoDescription"
                  value={formData.seoDescription}
                  onChange={handleInputChange}
                  placeholder="SEO Description for search engines"
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent outline-none"
                />
              </div>
            </div>

            <div className="flex gap-4 justify-end">
              <button
                type="button"
                onClick={() => setStep(3)}
                className="px-6 py-2 border border-gray-200 rounded-lg hover:bg-gray-50 transition text-gray-700 font-medium"
              >
                Back
              </button>
              <button
                type="submit"
                disabled={saving}
                className="flex items-center gap-2 px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition font-medium disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <Save size={18} />
                {saving ? 'Saving...' : isEdit ? 'Update Product' : 'Publish Product'}
              </button>
            </div>
          </div>
        )}
      </motion.form>
    </div>
  );
};

export default AddProduct;
